import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteContributorMutation, getContributorsListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteContributor = (page, limit, searchValue) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { deleteContributorPending: deleteContributorPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const [deleteContributorAction, { loading }] = useMutation(deleteContributorMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getContributorsListQuery,
                variables: {
                    elementsPerPage: limit,
                    orderBy: { created_at: "desc" },
                    page,
                    where: {
                        ...(searchValue && {
                            name: { contains: searchValue },
                        }),
                        ...(searchValue && {
                            name_ar: { contains: searchValue }, //eslint-disable-line
                        }),
                    },
                },
            },
        ],
    });

    const deleteContributor = async (id) => {
        try {
            await deleteContributorAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", { entity: t("contributors.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },

                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteContributorPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.deleteMsgs.pending", { entity: t("contributors.entity") }), {
                    toastId: `${deleteContributorPendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteContributor };
};

export default useDeleteContributor;
